/* Living-microbes overlay for the ecosystem section (lf-ecosystem.liquid).
 * Loaded with defer only when the section's "Animate visual" setting is on.
 * Renders 90-170 translucent rod/oval organisms drifting over the micrograph.
 * Population and opacity were raised on 2026-08-04: the original 40-80 at ~16%
 * mean alpha measured 0.9% effective ink and was invisible against the photo.
 * Degrades to a single static frame under prefers-reduced-motion, pauses
 * offscreen, caps at 30fps. No dependencies; zero instances = no-op.
 */
(() => {
  'use strict';

  if (customElements.get('lf-microbes-anim')) return;

  const FRAME_MS = 1000 / 30;
  const MIN_POP = 90;
  const MAX_POP = 170;
  const AREA_PER_ORGANISM = 3200; /* css px^2 per organism before clamping */

  /* Every motion constant lives here so tuning has exactly one home. Values are
   * the originals; hoisting them is a rename, not a retune. Seed velocities in
   * #spawn and the separation impulse in #divide stay where they are: those are
   * velocities in simulation units, not accelerations, and they already scale
   * through the clock below. */
  const MOTION = Object.freeze({
    jitter: 7,
    current: 1.2,
    damp: 0.5,
    cap: 9,
    spin: 0.2,
    spinMax: 0.12
  });

  /* Pointer nudge field. `accel` is calibrated EMPIRICALLY, not from the naive
   * drift equilibrium: the falloff averages ~0.33 over the field disk, so the
   * felt push is roughly accel * 0.33 / damp px/s. 8 at 100% strength gives a
   * felt ~5 px/s against a 1-2 px/s ambient drift, with the existing MOTION.cap
   * of 9 still bounding peak speed so the field can never fling an organism.
   * (The original 2.5 with a quadratic falloff averaged under 1 px/s and was
   * measurably invisible — headless probe 2026-08-04.) The swirl term keeps the
   * field reading as fluid rather than magnetic. A parked cursor fades to
   * nothing over idleFadeMs, so the effect stays a nudge and can never carve a
   * permanent hole. */
  const POINTER = Object.freeze({
    radiusFrac: 0.45,
    accel: 8,
    swirl: 0.35,
    idleFadeMs: 1200
  });

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const BODY_TINTS = [
    [185, 199, 205],
    [176, 192, 199],
    [197, 208, 213],
    [181, 197, 194]
  ];
  const OUTLINE = [104, 124, 134];
  const SHAPES = [3.1, 2.4, 1.7, 1.3]; /* major/minor axis ratios */
  const BLURS = [0, 1.6, 3.2]; /* baked into sprites; deeper = blurrier */

  /** @type {Array<Array<{canvas: HTMLCanvasElement, w: number, h: number}>>} */
  let sprites = null;

  const rand = (min, max) => min + Math.random() * (max - min);

  function capsulePath(g, x, y, w, h) {
    const r = h / 2;
    g.beginPath();
    g.moveTo(x + r, y);
    g.lineTo(x + w - r, y);
    g.arc(x + w - r, y + r, r, -Math.PI / 2, Math.PI / 2);
    g.lineTo(x + r, y + h);
    g.arc(x + r, y + r, r, Math.PI / 2, -Math.PI / 2);
    g.closePath();
  }

  function renderSprite(aspect, blur, tint) {
    const major = 120;
    const minor = major / aspect;
    const pad = Math.ceil(blur * 3) + 6;
    const c = document.createElement('canvas');
    c.width = Math.ceil(major + pad * 2);
    c.height = Math.ceil(minor + pad * 2);
    const g = c.getContext('2d');
    if (blur && 'filter' in g) g.filter = `blur(${blur}px)`;

    /* translucent body */
    capsulePath(g, pad, pad, major, minor);
    g.fillStyle = `rgba(${tint[0]}, ${tint[1]}, ${tint[2]}, 0.56)`;
    g.fill();

    /* soft darker membrane */
    g.lineWidth = 4.5;
    g.strokeStyle = `rgba(${OUTLINE[0]}, ${OUTLINE[1]}, ${OUTLINE[2]}, 0.78)`;
    g.stroke();

    /* brighter inner core */
    const inset = minor * 0.24;
    capsulePath(g, pad + inset, pad + inset, major - inset * 2, minor - inset * 2);
    g.fillStyle = 'rgba(232, 240, 242, 0.3)';
    g.fill();

    return { canvas: c, w: c.width, h: c.height };
  }

  function buildSprites() {
    if (sprites) return;
    sprites = SHAPES.map((aspect, s) =>
      BLURS.map((blur) => renderSprite(aspect, blur, BODY_TINTS[s % BODY_TINTS.length]))
    );
  }

  class LfMicrobesAnim extends HTMLElement {
    #canvas = null;
    #ctx = null;
    #organisms = [];
    #targetPop = MIN_POP;
    #density = 1;
    #popCap = MAX_POP;
    #rafId = 0;
    #lastFrame = 0;
    #elapsed = 0;
    #simTime = 0;
    #speed = 1;
    #visible = true;
    #io = null;
    #ro = null;
    #motionQuery = null;
    #onMotionChange = null;
    #pointerOn = true;
    #pointerStrength = 1;
    #pointerClientX = 0;
    #pointerClientY = 0;
    #pointerMoveMs = 0;
    #pointerBound = false;
    #onPointerMove = null;

    static get observedAttributes() {
      return ['data-speed', 'data-pointer', 'data-pointer-strength', 'data-density'];
    }

    attributeChangedCallback() {
      this.#readSettings();
      this.#applyDensity();
    }

    /* Merchant settings arrive as data attributes on the element. Parsed
     * defensively and clamped: a hand-edited or stale stored value must never
     * drive the simulation out of range. An absent data-pointer means on,
     * matching the schema default. Density is clamped to the schema's 50-200%
     * because the ceiling is performance-verified; a wilder stored value must
     * not buy more organisms than we have measured. */
    #readSettings() {
      const speed = parseFloat(this.dataset.speed);
      this.#speed = Number.isFinite(speed) ? clamp(speed / 100, 0.25, 4) : 1;
      this.#pointerOn = this.dataset.pointer !== 'false';
      const strength = parseFloat(this.dataset.pointerStrength);
      this.#pointerStrength = Number.isFinite(strength) ? clamp(strength / 100, 0, 4) : 1;
      const density = parseFloat(this.dataset.density);
      this.#density = Number.isFinite(density) ? clamp(density / 100, 0.5, 2) : 1;
      this.#popCap = Math.round(MAX_POP * this.#density);
    }

    /* Re-seat the population when density changes on a live element (the theme
     * editor reloads the whole section on a setting change, but a runtime
     * attribute flip must respond too). Top-ups fade in; an excess marks the
     * overflow as fading so the culture thins organically instead of popping. */
    #applyDensity() {
      if (!this.#ctx || !this.#organisms.length) return;
      this.#recomputeTarget();
      const list = this.#organisms;
      const alive = list.filter((o) => !o.fading).length;
      if (alive < this.#targetPop) {
        for (let i = alive; i < this.#targetPop; i++) list.push(this.#spawn(true));
      } else {
        let excess = alive - this.#targetPop;
        for (let i = list.length - 1; i >= 0 && excess > 0; i--) {
          if (!list[i].fading) { list[i].fading = true; excess--; }
        }
      }
      if (this.#reducedMotion()) this.#draw();
    }

    #recomputeTarget() {
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;
      const base = (w * h) / AREA_PER_ORGANISM;
      this.#targetPop = Math.round(clamp(base * this.#density, MIN_POP * this.#density, MAX_POP * this.#density));
    }

    /* The canvas is pointer-events: none by design (it must never intercept
     * clicks on the visual or in the theme editor), so a listener bound to the
     * element itself would never fire. One passive window listener per instance
     * instead, converted to element space with a single rect read per frame. */
    #bindPointer() {
      if (this.#pointerBound || !this.#onPointerMove || this.#reducedMotion()) return;
      window.addEventListener('pointermove', this.#onPointerMove, { passive: true });
      this.#pointerBound = true;
    }

    #unbindPointer() {
      if (!this.#pointerBound) return;
      window.removeEventListener('pointermove', this.#onPointerMove);
      this.#pointerBound = false;
    }

    connectedCallback() {
      this.#readSettings();
      this.#onPointerMove = (event) => {
        if (event.pointerType === 'touch' || this.#reducedMotion()) return;
        this.#pointerClientX = event.clientX;
        this.#pointerClientY = event.clientY;
        this.#pointerMoveMs = performance.now();
      };
      this.#canvas = this.querySelector('canvas');
      if (!this.#canvas) return;
      this.#ctx = this.#canvas.getContext('2d');
      if (!this.#ctx) return;

      buildSprites();

      this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#onMotionChange = () => this.#applyMotionPreference();
      if (typeof this.#motionQuery.addEventListener === 'function') {
        this.#motionQuery.addEventListener('change', this.#onMotionChange);
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.#ro = new ResizeObserver(() => this.#resize());
        this.#ro.observe(this);
      }

      if (typeof IntersectionObserver !== 'undefined') {
        this.#visible = false;
        this.#io = new IntersectionObserver((entries) => {
          for (const entry of entries) this.#visible = entry.isIntersecting;
          this.#applyMotionPreference();
        }, { rootMargin: '80px' });
        this.#io.observe(this);
      }

      this.#resize();
      this.#applyMotionPreference();
    }

    disconnectedCallback() {
      this.#stop();
      this.#unbindPointer();
      this.#io?.disconnect();
      this.#ro?.disconnect();
      if (this.#motionQuery && typeof this.#motionQuery.removeEventListener === 'function') {
        this.#motionQuery.removeEventListener('change', this.#onMotionChange);
      }
    }

    #reducedMotion() {
      return Boolean(this.#motionQuery && this.#motionQuery.matches);
    }

    #applyMotionPreference() {
      if (!this.#ctx) return;
      if (this.#reducedMotion()) {
        this.#unbindPointer();
        this.#stop();
        this.#draw();
        return;
      }
      this.#bindPointer();
      if (this.#visible) {
        this.#start();
      } else {
        this.#stop();
      }
    }

    #resize() {
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.#canvas.width = Math.round(w * dpr);
      this.#canvas.height = Math.round(h * dpr);
      this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.#recomputeTarget();
      if (!this.#organisms.length) {
        for (let i = 0; i < this.#targetPop; i++) {
          this.#organisms.push(this.#spawn(false));
        }
      }
      if (this.#reducedMotion()) this.#draw();
    }

    #spawn(fadeIn) {
      const depth = Math.random();
      const baseLen = rand(18, 44) * (1.1 - depth * 0.45);
      const o = {
        x: rand(0, this.clientWidth),
        y: rand(0, this.clientHeight),
        angle: rand(0, Math.PI * 2),
        vx: rand(-4, 4),
        vy: rand(-4, 4),
        va: rand(-0.08, 0.08),
        len: baseLen,
        baseLen,
        shape: Math.floor(Math.random() * SHAPES.length),
        blur: depth < 0.45 ? 0 : depth < 0.78 ? 1 : 2,
        baseAlpha: (0.82 - depth * 0.26) * rand(0.85, 1.15),
        alpha: 0,
        fading: false,
        phase: rand(0, Math.PI * 2),
        pulse: rand(0.4, 0.9)
      };
      if (!fadeIn) o.alpha = o.baseAlpha;
      return o;
    }

    #divide(parent) {
      const child = this.#spawn(true);
      const ax = Math.cos(parent.angle);
      const ay = Math.sin(parent.angle);
      child.x = parent.x + ax * parent.len * 0.55;
      child.y = parent.y + ay * parent.len * 0.55;
      child.angle = parent.angle + rand(-0.3, 0.3);
      child.baseLen = parent.baseLen * 0.82;
      child.len = child.baseLen * 0.7;
      child.shape = parent.shape;
      child.blur = parent.blur;
      /* slow separation along the shared axis */
      child.vx = parent.vx + ax * 5;
      child.vy = parent.vy + ay * 5;
      parent.vx -= ax * 5;
      parent.vy -= ay * 5;
      parent.len = parent.baseLen * 0.88;
      this.#organisms.push(child);
    }

    /* The merchant speed setting is a clock multiplier, not a set of new
     * constants. Motion terms run on `mdt`; the life cycle (birth, division,
     * fade, length and alpha easing) and the draw-time breathing wobble stay on
     * the real `dt`. The setting controls how fast organisms MOVE, not how fast
     * they are born and die, so population churn is identical at every speed.
     * Because damping and the ceiling ride the same scaled clock, velocity in
     * simulation units is unchanged and real displacement scales linearly:
     * the same trajectories, traversed faster. At 100% `mdt === dt` exactly. */
    #step(dt) {
      const W = this.clientWidth;
      const H = this.clientHeight;
      const mdt = dt * this.#speed;
      this.#simTime += mdt;
      const t = this.#simTime;
      const list = this.#organisms;

      /* Pointer field, computed once per frame. Idle and off-panel cases bail
       * out to force 0, which keeps the whole feature O(1) when nothing is
       * hovering. Coordinates are CSS pixels on both sides (the canvas transform
       * already absorbs devicePixelRatio), so no DPR math belongs here. */
      let px = 0;
      let py = 0;
      let r = 0;
      let r2 = 0;
      let force = 0;
      if (this.#pointerOn && this.#pointerStrength > 0 && this.#pointerMoveMs) {
        const influence = 1 - clamp((performance.now() - this.#pointerMoveMs) / POINTER.idleFadeMs, 0, 1);
        if (influence > 0) {
          const rect = this.getBoundingClientRect();
          px = this.#pointerClientX - rect.left;
          py = this.#pointerClientY - rect.top;
          r = Math.min(W, H) * POINTER.radiusFrac;
          r2 = r * r;
          const outside = px < -r || px > W + r || py < -r || py > H + r;
          if (!outside) force = POINTER.accel * this.#pointerStrength * influence;
        }
      }

      if (list.length < this.#targetPop && Math.random() < dt * 0.5) {
        list.push(this.#spawn(true));
      }

      for (let i = list.length - 1; i >= 0; i--) {
        const o = list[i];

        /* Brownian jitter + a faint slow current, heavily damped */
        o.vx += rand(-MOTION.jitter, MOTION.jitter) * mdt + Math.sin(t * 0.05 + o.y * 0.004) * MOTION.current * mdt;
        o.vy += rand(-MOTION.jitter, MOTION.jitter) * mdt + Math.cos(t * 0.04 + o.x * 0.004) * MOTION.current * mdt;

        /* Cursor push, applied as an acceleration BEFORE damping and the ceiling
         * so both absorb it: MOTION.damp decays the nudge organically over about
         * two seconds and MOTION.cap bounds it, so no extra clamping is needed
         * and the field can never fling an organism. Scaled by mdt, so the nudge
         * stays proportional to drift at every speed setting. */
        if (force > 0) {
          const dx = o.x - px;
          const dy = o.y - py;
          const d2 = dx * dx + dy * dy;
          if (d2 <= r2 && d2 > 0.001) {
            const d = Math.sqrt(d2);
            /* Linear falloff: quadratic averaged ~0.17 over the disk and made
             * the field measurably invisible at any sane accel. Damping plus
             * the swirl keep the edge soft enough without squaring. */
            const f = 1 - d / r;
            const nx = dx / d;
            const ny = dy / d;
            const push = force * f * mdt;
            o.vx += nx * push - ny * push * POINTER.swirl;
            o.vy += ny * push + nx * push * POINTER.swirl;
          }
        }

        const damp = Math.max(0, 1 - MOTION.damp * mdt);
        o.vx *= damp;
        o.vy *= damp;
        const speed = Math.hypot(o.vx, o.vy);
        if (speed > MOTION.cap) {
          o.vx *= MOTION.cap / speed;
          o.vy *= MOTION.cap / speed;
        }
        o.x += o.vx * mdt;
        o.y += o.vy * mdt;
        o.va += rand(-MOTION.spin, MOTION.spin) * mdt;
        o.va = clamp(o.va, -MOTION.spinMax, MOTION.spinMax);
        o.angle += o.va * mdt;
        o.len += (o.baseLen - o.len) * Math.min(1, dt * 0.8);

        /* wrap with a margin so bodies drift fully off before re-entering */
        const m = o.len;
        if (o.x < -m) o.x = W + m;
        else if (o.x > W + m) o.x = -m;
        if (o.y < -m) o.y = H + m;
        else if (o.y > H + m) o.y = -m;

        /* life cycle: ease alpha, occasionally fade out or divide */
        if (o.fading) {
          o.alpha -= o.baseAlpha * dt * 0.35;
          if (o.alpha <= 0) list.splice(i, 1);
        } else {
          o.alpha = Math.min(o.baseAlpha, o.alpha + o.baseAlpha * dt * 0.3);
          if (Math.random() < dt / 240) o.fading = true;
          else if (list.length < this.#popCap && Math.random() < dt / 260) this.#divide(o);
        }
      }
    }

    #draw() {
      const ctx = this.#ctx;
      const W = this.clientWidth;
      const H = this.clientHeight;
      ctx.clearRect(0, 0, W, H);

      for (const o of this.#organisms) {
        const sprite = sprites[o.shape][o.blur];
        const scale = o.len / 120;
        const wobble = 1 + 0.08 * Math.sin(this.#elapsed * o.pulse + o.phase);
        ctx.globalAlpha = Math.max(0, Math.min(1, o.alpha * wobble));
        ctx.save();
        ctx.translate(o.x, o.y);
        ctx.rotate(o.angle);
        ctx.drawImage(
          sprite.canvas,
          -sprite.w * scale / 2,
          -sprite.h * scale / 2,
          sprite.w * scale,
          sprite.h * scale
        );
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    }

    #start() {
      if (this.#rafId) return;
      this.#lastFrame = 0;
      const loop = (now) => {
        this.#rafId = requestAnimationFrame(loop);
        if (now - this.#lastFrame < FRAME_MS) return;
        const dt = this.#lastFrame ? Math.min((now - this.#lastFrame) / 1000, 0.1) : FRAME_MS / 1000;
        this.#lastFrame = now;
        this.#elapsed += dt;
        this.#step(dt);
        this.#draw();
      };
      this.#rafId = requestAnimationFrame(loop);
    }

    #stop() {
      if (!this.#rafId) return;
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
  }

  customElements.define('lf-microbes-anim', LfMicrobesAnim);
})();
